import { useEffect, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

import { useReport } from "../context/ReportContext";
import {
  COMBAT_LOG_BLOCK,
  COMBAT_LOG_METRIC,
  HIDDEN_METRICS,
  metricViews,
} from "../metrics";
import GenericTable from "../metrics/GenericTable";

export default function Report() {
  const params = useParams<{ slug: string; metric?: string }>();
  const slug = params.slug ?? "";
  const metricFromUrl = params.metric;

  const { report, error, setSlug } = useReport();

  // Tell the provider what to load. We deliberately don't clear the slug on
  // unmount — the two routes `/r/:slug` and `/r/:slug/:metric` are separate
  // Route elements, so the auto-redirect from the bare slug URL to the
  // metric URL causes an unmount + remount of this component. Clearing the
  // context in the cleanup would briefly nuke the report data, blanking the
  // page mid-navigation. The Sidebar already gates its rendering on
  // `useMatch("/r/...")` so stale context never leaks into /admin.
  useEffect(() => {
    setSlug(slug || null);
  }, [slug, setSlug]);

  const metricNames = useMemo(() => {
    if (!report) return [];
    // Trust the backend-provided order (Firestore map iteration is unstable).
    const ordered = report.metricOrder?.length
      ? report.metricOrder.filter((n) => n in report.metrics)
      : Object.keys(report.metrics);
    const names = ordered.filter((n) => !HIDDEN_METRICS.has(n));
    // Combat Log is a virtual section appended only when an enrichment exists.
    if (report.enrichment) names.push(COMBAT_LOG_METRIC);
    return names;
  }, [report]);

  if (error) return <p className="text-red-400">{error}</p>;
  // If we have a slug from the URL but no report yet, we're either:
  //   - on the very first render (effect hasn't fired → context still has
  //     no slug and `loading` is still false), or
  //   - mid-fetch.
  // Either way, show a loading state instead of returning null, otherwise
  // the user sees a blank page until the next render tick.
  if (!report) return <p className="text-gray-400">Loading…</p>;

  if (metricNames.length === 0) {
    return <p className="text-sm text-gray-500">No metrics on this report yet.</p>;
  }

  const activeMetric =
    metricFromUrl && metricNames.includes(metricFromUrl)
      ? metricFromUrl
      : metricNames[0];

  if (metricFromUrl !== activeMetric) {
    return <Navigate to={`/r/${slug}/${activeMetric}`} replace />;
  }

  const block =
    activeMetric === COMBAT_LOG_METRIC
      ? COMBAT_LOG_BLOCK
      : report.metrics[activeMetric];
  const View = metricViews[activeMetric] ?? GenericTable;

  return (
    <article className="space-y-5">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold tracking-tight">{block.title}</h1>
        {block.description && (
          <p className="text-sm text-gray-400">{String(block.description)}</p>
        )}
      </header>

      <section className="rounded-lg border border-border bg-panel p-4 shadow-sm">
        {block.error ? (
          <p className="text-red-400 text-sm">{String(block.error)}</p>
        ) : (
          <View block={block} />
        )}
        {block.note ? (
          <p className="mt-3 text-xs text-gray-500 italic">{String(block.note)}</p>
        ) : null}
      </section>
    </article>
  );
}
